import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type PlanType = 'subscription' | 'purchase';
type MaintenanceStatus = 'active' | 'expiring_soon' | 'expired' | 'paused';

type ChurchPlan = {
  id: number;
  authChurch: string;
  planType: PlanType;
  planName: string;
  monthlyFee: number | null;
  startedAt: string | null;
  expiresAt: string | null;
  maintenanceStatus: MaintenanceStatus;
  autoRenew: 0 | 1;
  note: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

const PLAN_TYPE_LABEL: Record<PlanType, string> = { subscription: '구독형', purchase: '구매형' };
const MAINTENANCE_LABEL: Record<MaintenanceStatus, string> = {
  active: '정상',
  expiring_soon: '만료 임박',
  expired: '만료됨',
  paused: '일시중지',
};
const MAINTENANCE_TAG: Record<MaintenanceStatus, string> = {
  active: 'signed',
  expiring_soon: 'pending',
  expired: 'pending',
  paused: 'pending',
};

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const EMPTY_FORM = {
  authChurch: '',
  planType: 'subscription' as PlanType,
  planName: '',
  monthlyFee: '',
  startedAt: '',
  expiresAt: '',
  maintenanceStatus: 'active' as MaintenanceStatus,
  autoRenew: false,
  note: '',
};

export default function PlanRenewal() {
  const session = getAdminSession();
  const [items, setItems] = useState<ChurchPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<ChurchPlan[]>>(`${MainURL}/admin-console/church-plans`);
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '요금제 현황을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '요금제 현황을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const startEdit = (plan: ChurchPlan) => {
    setForm({
      authChurch: plan.authChurch,
      planType: plan.planType,
      planName: plan.planName,
      monthlyFee: plan.monthlyFee != null ? String(plan.monthlyFee) : '',
      startedAt: plan.startedAt ? String(plan.startedAt).slice(0, 10) : '',
      expiresAt: plan.expiresAt ? String(plan.expiresAt).slice(0, 10) : '',
      maintenanceStatus: plan.maintenanceStatus,
      autoRenew: Boolean(plan.autoRenew),
      note: plan.note || '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.authChurch.trim()) {
      alert('authChurch 값을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/church-plans`, {
        requesterId: session.id,
        ...form,
        monthlyFee: form.monthlyFee === '' ? null : Number(form.monthlyFee),
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      setForm(EMPTY_FORM);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 교회의 요금제 정보를 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/church-plans/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">요금제 · 갱신 현황</h2>
          <p className="admin-panel-desc">교회별 요금제 만료일과 갱신 / 유지보수 여부를 관리합니다. (authChurch 기준 등록 - 이미 있으면 갱신됩니다)</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="authChurch"
          value={form.authChurch}
          onChange={(e) => setForm((prev) => ({ ...prev, authChurch: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <select value={form.planType} onChange={(e) => setForm((prev) => ({ ...prev, planType: e.target.value as PlanType }))}>
          <option value="subscription">구독형</option>
          <option value="purchase">구매형</option>
        </select>
        <input
          type="text"
          placeholder="플랜명"
          value={form.planName}
          onChange={(e) => setForm((prev) => ({ ...prev, planName: e.target.value }))}
          style={{ maxWidth: 140 }}
        />
        <input
          type="number"
          placeholder="월 요금"
          value={form.monthlyFee}
          onChange={(e) => setForm((prev) => ({ ...prev, monthlyFee: e.target.value }))}
          style={{ maxWidth: 120 }}
        />
        <input
          type="date"
          placeholder="시작일"
          value={form.startedAt}
          onChange={(e) => setForm((prev) => ({ ...prev, startedAt: e.target.value }))}
          style={{ maxWidth: 150 }}
        />
        <input
          type="date"
          placeholder="만료일"
          value={form.expiresAt}
          onChange={(e) => setForm((prev) => ({ ...prev, expiresAt: e.target.value }))}
          style={{ maxWidth: 150 }}
        />
        <select
          value={form.maintenanceStatus}
          onChange={(e) => setForm((prev) => ({ ...prev, maintenanceStatus: e.target.value as MaintenanceStatus }))}
        >
          {Object.entries(MAINTENANCE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <label className="admin-form-checkbox">
          <input
            type="checkbox"
            checked={form.autoRenew}
            onChange={(e) => setForm((prev) => ({ ...prev, autoRenew: e.target.checked }))}
          />
          자동갱신
        </label>
        <input
          type="text"
          placeholder="비고"
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
        />
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            저장
          </button>
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>교회</th>
              <th>구분</th>
              <th>플랜명</th>
              <th>월 요금</th>
              <th>만료일</th>
              <th>자동갱신</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={8}>등록된 요금제 정보가 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.authChurch}</td>
                  <td>{PLAN_TYPE_LABEL[item.planType] ?? item.planType}</td>
                  <td>{item.planName || '-'}</td>
                  <td>{item.monthlyFee != null ? `${item.monthlyFee.toLocaleString()}원` : '-'}</td>
                  <td>{item.expiresAt ? String(item.expiresAt).slice(0, 10) : '-'}</td>
                  <td>{item.autoRenew ? 'Y' : 'N'}</td>
                  <td>
                    <span className={`admin-status-tag admin-status-tag--${MAINTENANCE_TAG[item.maintenanceStatus]}`}>
                      {MAINTENANCE_LABEL[item.maintenanceStatus] ?? item.maintenanceStatus}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="admin-btn edit-btn" onClick={() => startEdit(item)}>
                      수정
                    </button>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
